/**
 * JsonViewer - Reusable component for displaying JSON data with
 * format toggle and copy-to-clipboard support.
 *
 * Accepts raw JSON strings or objects. Auto-detects if a string
 * is parseable JSON and offers a toggle between raw and formatted views.
 * Deep-parses stringified JSON values within objects (e.g., MCP responses
 * that wrap JSON as escaped strings inside content[].text).
 */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Braces, Copy, CheckCheck, WrapText } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { copyToClipboard } from '../../utils/clipboard';
import { Button } from '../../../components/ui/button';

interface JsonViewerProps {
  /** JSON data — object/array is always formattable; string is auto-detected */
  data: string | Record<string, unknown> | unknown[];
  /** Maximum height before scrolling (Tailwind class). Set to empty string to disable. */
  maxHeight?: string;
  /** Show copy button (default true) */
  copyable?: boolean;
  /** Start in formatted mode (default true for objects, false for strings) */
  defaultFormatted?: boolean;
  /** Additional class names for the outer container */
  className?: string;
  /** Label for the copy button tooltip */
  copyLabel?: string;
}

/** Try to parse a string as JSON. Returns the parsed object if valid, null otherwise. */
function tryParseJson(text: string): unknown | null {
  try {
    const parsed = JSON.parse(text);
    // Only treat objects/arrays as JSON, not primitives
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Unwrap a multi-stringified JSON string.
 * Handles values that were JSON.stringify'd multiple times:
 *   '"[{\\"id\\":\\"abc\\"}]"' → parse → '[{"id":"abc"}]' → parse → [{id:"abc"}]
 * Returns the unwrapped value, or the original string if not parseable.
 */
function unwrapStringifiedJson(str: string): unknown {
  let current: unknown = str;
  // Keep parsing while result is still a string (handles double/triple stringify)
  while (typeof current === 'string') {
    try {
      current = JSON.parse(current);
    } catch {
      break;
    }
  }
  // Return the unwrapped value if we reached an object/array, else original
  if (typeof current === 'object' && current !== null) {
    return current;
  }
  return str;
}

/**
 * Recursively parse stringified JSON values within an object.
 * Handles multiple layers of JSON.stringify escaping (common in MCP responses).
 * Turns { "preview": "{\"id\": 1}" } into { "preview": { "id": 1 } }
 */
function deepParseJson(obj: unknown): unknown {
  if (typeof obj === 'string') {
    const unwrapped = unwrapStringifiedJson(obj);
    if (unwrapped !== obj) {
      // Successfully unwrapped to an object/array — recurse into it
      return deepParseJson(unwrapped);
    }
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(deepParseJson);
  }
  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = deepParseJson(value);
    }
    return result;
  }
  return obj;
}

export function JsonViewer({
  data,
  maxHeight = 'max-h-[400px]',
  copyable = true,
  defaultFormatted,
  className,
  copyLabel,
}: JsonViewerProps) {
  const { t } = useTranslation('common');

  const isStringInput = typeof data === 'string';

  // For string inputs, try to parse as JSON
  const parsedFromString = useMemo(
    () => (isStringInput ? tryParseJson(data as string) : null),
    [data, isStringInput],
  );

  // Determine if we can format
  const canFormat = !isStringInput || parsedFromString !== null;

  // The actual object to format (with deep-parsed stringified JSON values)
  const jsonObject = useMemo(() => {
    const raw = isStringInput ? parsedFromString : data;
    return raw ? deepParseJson(raw) : null;
  }, [data, isStringInput, parsedFromString]);

  const [formatted, setFormatted] = useState(
    defaultFormatted ?? !isStringInput,
  );

  const [copied, setCopied] = useState(false);

  const formattedText = useMemo(
    () => (jsonObject ? JSON.stringify(jsonObject, null, 2) : ''),
    [jsonObject],
  );

  const rawText = useMemo(
    () => (isStringInput ? (data as string) : JSON.stringify(data)),
    [data, isStringInput],
  );

  const displayText = formatted && canFormat ? formattedText : rawText;

  const handleCopy = useCallback(async () => {
    await copyToClipboard(displayText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [displayText]);

  return (
    <div className={cn('relative group', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-end gap-0.5 mb-1">
        {canFormat && (
          <Button
            variant={formatted ? 'secondary' : 'ghost'}
            size="sm"
            className="h-5 px-1.5 text-[10px] gap-1"
            onClick={() => setFormatted(!formatted)}
            title={t('app.formatJson')}
          >
            {formatted ? (
              <WrapText className="h-3 w-3" />
            ) : (
              <Braces className="h-3 w-3" />
            )}
            {t('app.formatJson')}
          </Button>
        )}
        {copyable && (
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={handleCopy}
            title={copyLabel ?? t('app.copy')}
          >
            {copied ? (
              <CheckCheck className="h-3 w-3 text-success" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>

      {/* Content */}
      <pre
        className={cn(
          'text-[11px] leading-relaxed font-mono',
          'bg-muted/50 rounded-md p-3 border',
          'text-muted-foreground',
          'whitespace-pre-wrap break-all',
          maxHeight && 'overflow-y-auto overflow-x-hidden',
          maxHeight,
        )}
      >
        {displayText}
      </pre>
    </div>
  );
}
